<script module lang="ts">
  import type { PermissionState } from "@kontextwerk/control-web-frame/logic/permissions/model"
  import type { OrganizationDetail as CustomerOrganizationDetailApi } from "@kontextwerk/control-web-client"
  import type { CustomerFormModel, CustomersViewFrameContext } from "./context"
</script>

<script lang="ts">
  import { onDestroy, onMount } from "svelte"
  import { OrgRootBrandingMount, frameConfig } from "@kontextwerk/control-web-frame"
  import {
    ActivityContextPanel,
    composeControlActivityCatalog,
  } from "@kontextwerk/control-web-frame"
  import { getAdminApi } from "../../../../../../../../api/kehrblatt/client"
  import PageTemplate from "@kontextwerk/web-sdk/ui/layout/PageTemplate.svelte"
  import type { RecordDetailConfig } from "@kontextwerk/web-sdk/ui/layout/recordDetail/types"
  import { buildCustomerHeader, customerRecordIcon } from "./_logic/header"
  import { newNotification } from "@kontextwerk/web-sdk/core/overlay/notifications"
  import { location } from "../../../../../../../../_shared/_logic/store"
  import { spaNavigate } from "../../../../../../../../_shared/_logic/navigation"
  import { fetchOrganization } from "../../../../../../../../api/kehrblatt/organizations"
  import {
    fetchCustomerDetailProjection,
    updateCustomer,
  } from "../../../../../../../../api/kehrblatt/customer-registry/customers"
  import {
    defaultChannelPriorities,
    normalizeChannelPriorities,
  } from "../../../../../../_shared/_logic/channel-priorities"
  import {
    registerEventCallback,
    unregisterEventCallback,
  } from "../../../../../../../_shared/_logic/eventbus"
  import { updateAssistContext } from "../../../../../../_shared/_logic/assist-index"
  import { buildAppPermissionState } from "../../../../../../../_shared/_logic/permissions-model"
  import type { OrganizationDetail as OrganizationDetailApi } from "@kontextwerk/control-web-client"
  import CustomerDetailsPage from "./pages/CustomerDetails"
  import AddressesPage from "./pages/Addresses"
  import CustomerArchiveActions from "./_modules/customer-archive-actions"
  import { buildCustomerDetailViewNavigation, buildCustomerDetailViewPaths } from "./navigation"
  import { getKehrblattControlAuthContext } from "../../../../../../../_shared/_logic/auth-context"
  import {
    isFreeTrialExperienceOrganization,
    isFreeTrialRestrictionActive,
  } from "../../../../../../_shared/_logic/free-trial-experience"
  import { archivePresentation } from "../../../../../../_shared/_logic/archive-presentation"

  let {
    organizationId,
    organizationSlug = null,
    organizationResolvedId = null,
    customerId,
  } = $props<{
    organizationId: string
    organizationSlug?: string | null
    organizationResolvedId?: string | null
    customerId: string
  }>()
  const authContext = getKehrblattControlAuthContext()
  const emptyPermissions = buildAppPermissionState(null)
  const activityCatalog = $derived(composeControlActivityCatalog($frameConfig?.activity?.catalog))

  const uuidPattern =
    /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/
  const isUuid = (value: string | null | undefined) => Boolean(value && uuidPattern.test(value))

  let organization = $state<OrganizationDetailApi | null>(null)
  const apiOrganizationId = $derived(
    organization?.id ?? organizationResolvedId ?? (isUuid(organizationId) ? organizationId : null),
  )
  let customer = $state<Customer | null>(null)
  let contactPoints = $state<ContactPoint[]>([])
  let properties = $state<Property[]>([])
  let propertyLinks = $state<PropertyCustomer[]>([])
  let loading = $state(true)
  let error = $state<string | null>(null)
  let importRefreshTimeout: ReturnType<typeof setTimeout> | null = null

  let customerSaving = $state(false)
  let form = $state<CustomerFormModel>(resetCustomerForm())

  const decodeSegment = (value: string | null | undefined) => {
    if (typeof value !== "string") return null

    try {
      return decodeURIComponent(value)
    } catch {
      return value
    }
  }
  const effectiveOrgSegment = $derived(organization?.slug ?? null)
  const basePath = $derived(
    effectiveOrgSegment && customerId
      ? `/organizations/${encodeURIComponent(effectiveOrgSegment)}/customers/${customerId}`
      : "",
  )
  const tabPaths = $derived(buildCustomerDetailViewPaths(basePath))
  const normalizedPath = $derived(
    (() => {
      const raw = $location.path ?? "/"
      const trimmed = raw.replace(/\/+$/, "")

      return trimmed || "/"
    })(),
  )
  const pathOrgSegment = $derived.by(() => {
    const segments = normalizedPath.split("/").filter(Boolean)

    if (segments[0] === "organizations" && segments[1]) {
      return decodeSegment(segments[1])
    }

    return null
  })
  const organizationIdentifier = $derived(
    organizationResolvedId ?? organizationId ?? organizationSlug ?? pathOrgSegment ?? null,
  )
  const brandingRouteScopeKey = $derived.by(() => {
    const orgSegment =
      organization?.slug ?? pathOrgSegment ?? organizationSlug ?? organizationId ?? null

    return orgSegment ? `organization:${orgSegment}` : null
  })
  const administrationPath = $derived(
    `/organizations/${encodeURIComponent(organization?.slug ?? organizationSlug ?? pathOrgSegment ?? organizationId)}/administration`,
  )
  const customersListPath = $derived(`${administrationPath}/customers`)

  $effect(() => {
    if (!basePath || !tabPaths.details) return

    const onLegacyPrioritiesPath =
      normalizedPath === tabPaths.priorities || normalizedPath.startsWith(`${tabPaths.priorities}/`)

    if (normalizedPath === basePath || onLegacyPrioritiesPath) {
      spaNavigate(tabPaths.details, { replace: true, noSearchParam: false })
    }
  })

  $effect(() => {
    if (!organization?.slug || !pathOrgSegment) return

    if (pathOrgSegment === organization.slug) return

    const remainder =
      normalizedPath.replace(/^\/organizations\/[^/]+/, "") ||
      `/customers/${encodeURIComponent(customerId)}/details`
    const target = `/organizations/${encodeURIComponent(organization.slug)}${
      remainder.startsWith("/") ? remainder : `/${remainder}`
    }`
    spaNavigate(target, { replace: true, noSearchParam: false })
  })

  const permissions = $derived($authContext?.permissions ?? emptyPermissions)
  const currentUserRole = $derived($authContext?.currentUserRole ?? null)
  const currentUserOrganizationId = $derived($authContext?.currentUserOrganizationId ?? null)
  const isFreeTrialExperience = $derived(isFreeTrialExperienceOrganization(organization))
  const trialRestrictionsActive = $derived(
    isFreeTrialRestrictionActive(organization, permissions.isSuper),
  )
  const canManageArchive = $derived(
    !trialRestrictionsActive && permissions.allows("termin.scheduling", "manage"),
  )
  const customerSubtitle = $derived.by(() => {
    const base = organization?.name ?? "Ansprechpartner"

    if (!customer?.archived_at) return base

    return `${base} · ${archivePresentation(customer.archived_at, customer.archive_reason)}`
  })
  const assistTaskHint = $derived.by(() => {
    if (normalizedPath.startsWith(tabPaths.addresses)) return "ADDRESSES"

    if (normalizedPath.startsWith(tabPaths.contacts)) return "CONTACTS"

    return "CUSTOMER_DETAILS"
  })

  const normalizedCustomerPriorities = $derived(
    customer ? normalizeChannelPriorities(customer.channel_priorities) : null,
  )
  const normalizedFormPriorities = $derived(normalizeChannelPriorities(form.channel_priorities))

  const hasCustomerChanges = $derived(
    Boolean(
      customer &&
      (form.display_name.trim() !== customer.display_name ||
        form.type !== customer.type ||
        form.customer_facing_name_mode !== customer.customer_facing_name_mode ||
        JSON.stringify(normalizedFormPriorities) !== JSON.stringify(normalizedCustomerPriorities) ||
        (form.notes.trim() || "") !== (customer.notes ?? "")),
    ),
  )
  const effectiveOrganizationId = $derived(organization?.id ?? apiOrganizationId ?? null)
  const confirmedCustomer = $derived(
    customer?.id === customerId && !error && customer?.organization_id === effectiveOrganizationId
      ? customer
      : null,
  )
  // The record frame stands from the route on; the loaded customer only fills the header.
  const recordDetail = $derived<RecordDetailConfig>({
    key: `customer:${organizationId}:${customerId}`,
    loading: loading && !confirmedCustomer,
    header: confirmedCustomer
      ? buildCustomerHeader(confirmedCustomer, contactPoints)
      : { icon: customerRecordIcon, fields: [] },
  })

  function resetCustomerForm(): CustomerFormModel {
    return {
      display_name: "",
      type: "PERSON",
      customer_facing_name_mode: "USE_DISPLAY_NAME",
      channel_priorities: defaultChannelPriorities(),
      notes: "",
    }
  }

  function resetFormFromCustomer(nextCustomer: Customer | null = customer) {
    if (!nextCustomer) return

    form = {
      display_name: nextCustomer.display_name,
      type: nextCustomer.type,
      customer_facing_name_mode: nextCustomer.customer_facing_name_mode ?? "USE_DISPLAY_NAME",
      channel_priorities: normalizeChannelPriorities(nextCustomer.channel_priorities),
      notes: nextCustomer.notes ?? "",
    }
  }

  let loadRevision = 0
  const load = async () => {
    const orgIdentifier = organizationIdentifier
    const recordIdentifier = customerId

    if (!orgIdentifier || !recordIdentifier) return

    const revision = ++loadRevision
    const isCurrent = () =>
      revision === loadRevision &&
      orgIdentifier === organizationIdentifier &&
      recordIdentifier === customerId
    loading = true
    error = null
    try {
      const [orgPayload, detailPayload] = await Promise.all([
        fetchOrganization(orgIdentifier),
        fetchCustomerDetailProjection(recordIdentifier),
      ])

      if (!isCurrent()) return

      organization = orgPayload
      const customerPayload = detailPayload.customer
      customer = customerPayload
      resetFormFromCustomer(customerPayload)
      contactPoints = detailPayload.contact_points ?? []
      propertyLinks = detailPayload.property_customers ?? []
      properties = detailPayload.properties ?? []
    } catch (err) {
      if (!isCurrent()) return

      console.error("customer_detail_load_failed", err)
      error = "Ansprechpartner konnte nicht geladen werden."
    } finally {
      if (isCurrent()) loading = false
    }
  }

  $effect(() => {
    const orgId = organizationIdentifier
    const custId = customerId

    if (orgId && custId) {
      void load()
    }

    return () => {
      loadRevision += 1
    }
  })

  $effect(() => {
    if (!customer) return

    updateAssistContext({
      page: "CUSTOMER_DETAIL",
      taskHint: assistTaskHint,
      organizationId: organization?.id ?? apiOrganizationId ?? null,
      customerId: customer.id,
    })
  })

  type AssistEventPayload = {
    name?: string
    normalizedName?: string
    parameters?: Record<string, unknown>
  }

  const matchesCustomer = (payload: AssistEventPayload) => {
    const params = payload.parameters ?? {}
    const targetOrg =
      (params.organization_id as string | undefined) ??
      (params.organizationId as string | undefined) ??
      (params.org_id as string | undefined)

    if (targetOrg && effectiveOrganizationId && targetOrg !== effectiveOrganizationId) {
      return false
    }

    const targetCustomer =
      (params.customer_id as string | undefined) ??
      (params.customerId as string | undefined) ??
      (params.id as string | undefined)

    if (targetCustomer && targetCustomer !== customerId) {
      return false
    }

    const normalized = (payload.normalizedName ?? payload.name ?? "").toLowerCase()

    return Boolean(
      normalized.includes("customer") ||
      normalized.includes("address") ||
      normalized.includes("contact"),
    )
  }

  const registerAssistHandler = () => {
    const handlerId = `customer-detail-${customerId}`
    const handler = (payload: AssistEventPayload) => {
      if (!matchesCustomer(payload)) return

      void load()
    }
    registerEventCallback("assist.event", handlerId, handler)
    const stop = () => unregisterEventCallback("assist.event", handlerId)

    return stop
  }

  let unregister: (() => void) | null = null
  onMount(() => {
    unregister = registerAssistHandler()
  })
  onDestroy(() => {
    unregister?.()

    if (importRefreshTimeout) {
      clearTimeout(importRefreshTimeout)
      importRefreshTimeout = null
    }
  })

  const scheduleImportRefresh = () => {
    if (importRefreshTimeout) return

    importRefreshTimeout = setTimeout(() => {
      importRefreshTimeout = null
      void load()
    }, 400)
  }

  onMount(() => {
    const handlerId = `customer-import-${customerId}`
    const handler = () => scheduleImportRefresh()
    registerEventCallback("assist.import_progress", handlerId, handler)

    return () => unregisterEventCallback("assist.import_progress", handlerId)
  })

  const saveCustomer = async (canEdit: boolean) => {
    if (!customer || !canEdit || customerSaving) return

    customerSaving = true
    try {
      const payload: CustomerUpdatePayload = {
        display_name: form.display_name.trim(),
        type: form.type,
        customer_facing_name_mode: form.customer_facing_name_mode,
        channel_priorities: normalizedFormPriorities,
        notes: form.notes.trim() ? form.notes.trim() : null,
      }
      const updated = await updateCustomer(customer.id, payload)
      customer = updated
      resetFormFromCustomer(updated)
    } catch (err) {
      console.error("customer_update_failed", err)
      newNotification({
        class: "error",
        html: "Ansprechpartnerdaten konnten nicht gespeichert werden.",
      })
    } finally {
      customerSaving = false
    }
  }

  const handleContactsChanged = (contacts: ContactPoint[]) => {
    contactPoints = contacts
  }

  const handleAddressesChanged = (payload: {
    properties: Property[]
    links: PropertyCustomer[]
  }) => {
    properties = payload.properties
    propertyLinks = payload.links
  }

  const viewFrameContext = $derived.by<CustomersViewFrameContext>(() => ({
    permissions,
    organization,
    effectiveOrganizationId,
    customer,
    loading,
    error,
    form,
    customerSaving,
    hasCustomerChanges,
    contactPoints,
    properties,
    propertyLinks,
    isFreeTrialExperience,
    trialRestrictionsActive,
    onContactsChanged: handleContactsChanged,
    onAddressesChanged: handleAddressesChanged,
    onRetry: load,
    onResetForm: resetFormFromCustomer,
    onSaveCustomer: saveCustomer,
    currentUserRole,
    currentUserOrganizationId,
  }))

  const pageNavigation = $derived.by(() =>
    buildCustomerDetailViewNavigation({
      context: viewFrameContext,
      paths: tabPaths,
      snippets: {
        customerDetails: customerDetailsPageContent,
        addresses: addressesPageContent,
      },
    }),
  )
</script>

<OrgRootBrandingMount
  organizationId={effectiveOrganizationId}
  routeScopeKey={brandingRouteScopeKey}
  source="kehrblatt-customer-detail"
/>

{#snippet customerDetailsPageContent(activeTab: string)}
  <CustomerDetailsPage {viewFrameContext} {activeTab} />
{/snippet}

{#snippet addressesPageContent(activeTab: string)}
  <AddressesPage {viewFrameContext} {activeTab} />
{/snippet}

{#if confirmedCustomer}
  <ActivityContextPanel
    context={{
      organization_id: confirmedCustomer.organization_id,
      object_type: "customer",
      object_id: confirmedCustomer.id,
    }}
    label={confirmedCustomer.display_name}
    operations={getAdminApi().activity}
    catalog={activityCatalog}
  />
{/if}
<CustomerArchiveActions
  customer={confirmedCustomer}
  disabled={!canManageArchive}
  onChanged={(updated) => {
    customer = updated
    resetFormFromCustomer(updated)
  }}
>
  {#snippet children(actions)}
    <PageTemplate
      viewKey={`customer:${organizationId}:${customerId}`}
      title={customer?.display_name ?? "Ansprechpartner"}
      subtitle={customerSubtitle}
      {pageNavigation}
      appBreadcrumbs={[
        { title: "Verwaltungsbereich", href: administrationPath },
        { title: "Ansprechpartner", href: customersListPath },
        { title: customer?.display_name ?? "Ansprechpartner" },
      ]}
      locationStore={location}
      gobackLink={customersListPath}
      recordDetail={{ ...recordDetail, actions }}
    />
  {/snippet}
</CustomerArchiveActions>
